import React, { useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider as ReduxProvider } from 'react-redux';
import { legacy_createStore as createStore } from 'redux';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { ApolloProvider } from '~/lazy_apollo/client';
import { writeQueries } from '~/utils/apollo';
import loadApolloErrorMessages from '~/utils/loadApolloErrorMessages';
import { setApolloClient } from '../../../utils/apolloClient';
import { isSSR } from '../../../utils/dom';
import { initApollo } from '../../../utils/initApollo';
import { DefaultPopmenuConfig, PopmenuConfigContext } from '../../../utils/withPopmenuConfig';
import { ThemeProvider } from '../../../utils/withStyles';
import SnackbarProvider from '../../../shared/SnackbarProvider';
import WindowSizeProvider from '../../../shared/WindowSizeProvider';
import { type PopmenuConfigType, type PreloadedQuery, type EatAppProps } from '../../types/EatAppProps';

// Shared form components (BasicForm's snackbar hook) dispatch through Redux. Eat keeps no app state in Redux,
// so a store that ignores every action is enough.
export const reduxStore = createStore((state: Record<string, never> = {}) => state);

type ProvidersProps = EatAppProps & {
  children: React.ReactNode
}

type ApolloProps = {
  children: React.ReactNode
  operationStoreEnabled: boolean
  preloadQueries: PreloadedQuery[]
}

type PopmenuConfigProps = {
  children: React.ReactNode
  popmenuConfig: PopmenuConfigType
}

type RouterProps = {
  children: React.ReactNode
  requestPath: string
}

const Apollo = ({ children, operationStoreEnabled, preloadQueries }: ApolloProps) => {
  const client = initApollo({
    operationStoreEnabled,
    ssr: isSSR,
    url: '/graphql',
  });
  loadApolloErrorMessages();
  writeQueries(preloadQueries, client);
  setApolloClient(client);
  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
};

const PopmenuConfig = ({ children, popmenuConfig }: PopmenuConfigProps) => {
  const value = useMemo(() => ({
    ...DefaultPopmenuConfig,
    ...popmenuConfig,
  }), [popmenuConfig]);
  return (
    <PopmenuConfigContext.Provider value={value}>
      {children}
    </PopmenuConfigContext.Provider>
  );
};

const Router = ({ children, requestPath }: RouterProps) => {
  if (isSSR) {
    return (
      <StaticRouter location={requestPath} context={{}}>
        {children}
      </StaticRouter>
    );
  }
  return (
    <BrowserRouter>
      {children}
    </BrowserRouter>
  );
};

export const Providers = ({ children, operationStoreEnabled, popmenuConfig, preloadQueries, requestPath }: ProvidersProps) => (
  <Apollo operationStoreEnabled={operationStoreEnabled} preloadQueries={preloadQueries}>
    <IntlProvider locale="en">
      <ThemeProvider>
        <PopmenuConfig popmenuConfig={popmenuConfig}>
          <Router requestPath={requestPath}>
            <WindowSizeProvider desktop tablet={false} mobile={false}>
              <ReduxProvider store={reduxStore}>
                <SnackbarProvider>
                  {children}
                </SnackbarProvider>
              </ReduxProvider>
            </WindowSizeProvider>
          </Router>
        </PopmenuConfig>
      </ThemeProvider>
    </IntlProvider>
  </Apollo>
);
